// Shareable / deep-linkable view state encoded in the URL hash.
//
// The COMPLETE view is captured: map center (lat,lng), zoom, the selected
// time-slice (week index) and the selected allergens (by their STABLE manifest
// codes, e.g. BETU,POAC — never by index, so the link survives a manifest
// reorder or adding regions). The hash is used (not query params) so the link
// is fully client-side and never hits the server; updates use replaceState so
// scrubbing/panning never floods the browser history.
//
//   #lat=54.0000&lng=15.0000&z=3.40&w=22&a=BETU,POAC
//
// Restore order is handled by the caller (boot): center/zoom are applied at map
// construction; week + allergens after the map's "load".

export interface ViewState {
  lat: number;
  lng: number;
  zoom: number;
  week: number;
  codes: string[];
}

/** Parse the current location.hash into a partial ViewState (missing keys omitted). */
export function parseHash(hash: string = location.hash): Partial<ViewState> {
  const out: Partial<ViewState> = {};
  const raw = hash.replace(/^#/, "");
  if (!raw) return out;
  const params = new URLSearchParams(raw);

  const lat = num(params.get("lat"));
  const lng = num(params.get("lng"));
  if (lat !== null && lng !== null && Math.abs(lat) <= 90 && Math.abs(lng) <= 360) {
    out.lat = lat;
    out.lng = lng;
  }
  const z = num(params.get("z"));
  if (z !== null && z >= 0 && z <= 24) out.zoom = z;

  const w = intNum(params.get("w"));
  if (w !== null && w >= 0) out.week = w;

  const a = params.get("a");
  if (a !== null) {
    out.codes = a
      .split(",")
      .map((s) => s.trim().toUpperCase())
      .filter((s) => /^[A-Z0-9]+$/.test(s));
  }
  return out;
}

/** Build the hash string (no leading '#') for a full ViewState. */
export function buildHash(s: ViewState): string {
  const p = new URLSearchParams();
  p.set("lat", s.lat.toFixed(4));
  p.set("lng", s.lng.toFixed(4));
  p.set("z", s.zoom.toFixed(2));
  p.set("w", String(s.week));
  // Keep allergens last + un-encoded commas for a clean, human-readable link.
  const a = s.codes.join(",");
  let q = p.toString();
  if (a) q += `&a=${a}`;
  return q;
}

/**
 * Debounced URL writer. Call `schedule(getState)` on every interaction; the hash
 * is rewritten (via replaceState) at most once per `delayMs`. The getter is
 * read lazily at flush time so the very latest state is always written.
 */
export class UrlWriter {
  private timer: number | null = null;
  private last = "";

  constructor(
    private getState: () => ViewState,
    private delayMs = 350,
  ) {}

  schedule(): void {
    if (this.timer !== null) window.clearTimeout(this.timer);
    this.timer = window.setTimeout(() => this.flush(), this.delayMs);
  }

  /** Write immediately (used on important state changes if desired). */
  flush(): void {
    if (this.timer !== null) {
      window.clearTimeout(this.timer);
      this.timer = null;
    }
    const hash = buildHash(this.getState());
    if (hash === this.last) return;
    this.last = hash;
    const url = `${location.pathname}${location.search}#${hash}`;
    try {
      history.replaceState(history.state, "", url);
    } catch {
      // Some sandboxed contexts disallow replaceState on file://; fall back to
      // assigning the hash (still no history spam for identical values).
      location.hash = hash;
    }
  }
}

function num(v: string | null): number | null {
  if (v === null || v.trim() === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function intNum(v: string | null): number | null {
  const n = num(v);
  return n === null ? null : Math.round(n);
}
